import React, { useState } from 'react';
import config from '../../../config';
import { locales, defaultLocale } from '../../i18n/locales';
import TreeNode from './treeNode';

const calculateTreeData = (edges, locale) => {
  const localeConfig = locales[locale] || locales[defaultLocale];
  const localeSlug = localeConfig.slug;
  const isDefault = localeConfig.isDefault;

  // Strip locale prefix from a slug to get content-only path
  const stripLocale = (slug) => {
    if (!isDefault && localeSlug && slug.startsWith(`/${localeSlug}/`)) {
      return slug.substring(localeSlug.length + 1);
    }
    if (!isDefault && slug === `/${localeSlug}`) {
      return '/';
    }
    return slug;
  };

  const rootSlug = isDefault ? '/' : `/${localeSlug}`;

  const originalData = config.sidebar.ignoreIndex
    ? edges.filter(
      ({
         node: {
           fields: { slug },
         },
       }) => slug !== '/' && slug !== rootSlug
    )
    : edges;

  const tree = originalData.reduce(
    (
      accu,
      {
        node: {
          fields: { slug, title },
          frontmatter: { order }
        },
      }
    ) => {
      // Use the content-only path for tree building
      const contentSlug = stripLocale(slug);
      const parts = contentSlug.split('/');

      let { items: prevItems } = accu;

      const slicedParts =
        config.gatsby && config.gatsby.trailingSlash ? parts.slice(1, -2) : parts.slice(1, -1);

      for (const part of slicedParts) {
        let tmp = prevItems && prevItems.find(({ label }) => label === part);

        if (tmp) {
          if (!tmp.items) {
            tmp.items = [];
          }
        } else {
          tmp = { label: part, items: [] };
          prevItems.push(tmp);
        }
        prevItems = tmp.items;
      }
      const slicedLength =
        config.gatsby && config.gatsby.trailingSlash ? parts.length - 2 : parts.length - 1;

      const existingItem = prevItems.find(({ label }) => label === parts[slicedLength]);

      if (existingItem) {
        existingItem.url = slug; // Keep the full slug (with locale) for URL
        existingItem.title = title;
        existingItem.order = order;
      } else {
        prevItems.push({
          label: parts[slicedLength],
          url: slug, // Keep the full slug (with locale) for URL
          items: [],
          title,
          order,
        });
      }
      return accu;
    },
    { items: [] }
  );

  const {
    sidebar: { forcedNavOrder = [] },
  } = config;

  const tmp = [...forcedNavOrder];

  if (config.gatsby && config.gatsby.trailingSlash) {
    tmp.reverse();
  }

  tmp.reverse();

  const sortItems = items => {
    if (!items) return [];
    items.forEach(item => {
      if (item && item.items && item.items.length) {
        item.items = sortItems(item.items);
      }
    });

    return items.sort((a, b) => (a.order || 0) - (b.order || 0));
  };

  tree.items = sortItems(tree.items);

  // forcedNavOrder uses content-only paths (e.g., /guides, /knowledge-base)
  // We match by label (the directory name), which is the same regardless of locale
  return tmp.reduce((accu, slug) => {
    const parts = slug.split('/');

    let { items: prevItems } = accu;

    const slicedParts =
      config.gatsby && config.gatsby.trailingSlash ? parts.slice(1, -2) : parts.slice(1, -1);

    for (const part of slicedParts) {
      let tmp = prevItems.find(item => item && item.label === part);

      if (tmp) {
        if (!tmp.items) {
          tmp.items = [];
        }
      } else {
        tmp = { label: part, items: [] };
        prevItems.push(tmp);
      }
      if (tmp && tmp.items) {
        prevItems = tmp.items;
      }
    }

    if (prevItems) {
      prevItems.forEach(item => {
        if (item && item.items) {
          item.items = sortItems(item.items);
        }
      });
    }

    const slicedLength =
      config.gatsby && config.gatsby.trailingSlash ? parts.length - 2 : parts.length - 1;

    const index = prevItems.findIndex(({ label }) => label === parts[slicedLength]);

    if (prevItems.length && index >= 0) {
      accu.items.unshift(prevItems.splice(index, 1)[0]);
    }
    return accu;
  }, tree);
};

const Tree = ({ edges, locale }) => {
  const currentLocale = locale || defaultLocale;
  let [treeData] = useState(() => {
    return calculateTreeData(edges, currentLocale);
  });

  const localeConfig = locales[currentLocale] || locales[defaultLocale];
  const localeSlug = localeConfig.slug;
  const isDefault = localeConfig.isDefault;

  const defaultCollapsed = {};

  treeData.items.forEach(item => {
    // collapsedNav uses content-only paths, so we match by constructing the full URL
    const fullUrl = item.url;
    const contentUrl = isDefault ? fullUrl : (fullUrl && fullUrl.startsWith(`/${localeSlug}`) ? fullUrl.substring(localeSlug.length + 1) : fullUrl);

    if (config.sidebar.collapsedNav && config.sidebar.collapsedNav.includes(contentUrl)) {
      defaultCollapsed[fullUrl] = true;
    } else {
      defaultCollapsed[fullUrl] = false;
    }
  });

  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  const toggle = url => {
    setCollapsed({
      ...collapsed,
      [url]: !collapsed[url],
    });
  };

  return (
    <TreeNode
      className={`${config.sidebar.frontLine ? 'showFrontLine' : 'hideFrontLine'} firstLevel`}
      setCollapsed={toggle}
      collapsed={collapsed}
      {...treeData}
    />
  );
};

export default Tree;
